/**
 * -----------------------------------------------------------------------------
 * Enterprises Listing Page
 * -----------------------------------------------------------------------------
 * This sysadmin page is used to list all the enterprises.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useListEnterprisesQuery } from "../../../api/network/sysadminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { OrganizationUser } from "../../../api/types/Admin";
import { TEnterprise } from "../../../api/types/Sysadmin";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import SysadminEnterprisesCreateNew from "./createNewEnterprise";
import { useNavigate } from "react-router-dom";

const all_columns = [
  // "Sr. No",
  "Account ID",
  "Account Description",
  "Contact Name",
  "Contact Number",
  "Timezone",
  "Active Assets",
  "Status",
  "Action",
];

const ScreenSysadminEnterprises = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "sysadmin.enterprises",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { sysadminEnterprises: sysadminEnterprisesQueryParams } =
    listingQueryParams;
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.Enterprises
  );
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        sysadminEnterprises: {
          ...sysadminEnterprisesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  //LOGIN
  const navigate = useNavigate();
  const handleLogin = (accountId: string, e?: React.MouseEvent) => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log(`Login button clicked for account ID: ${accountId}`);
    e?.stopPropagation();
    const newUrl = routeUrls.sysadminChildren.account_users.replace(
      ":accountId",
      `${accountId}`
    );
    navigate(newUrl);
  };

  // fetching user data
  const {
    data: dataEnterprises,
    isFetching: isFetchingEnterprises,
    error,
  } = useListEnterprisesQuery(sysadminEnterprisesQueryParams, {
    skip: !thisUserOrganizationId,
  });
  const { count, next, previous, results } = dataEnterprises || {};

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as OrganizationUser[])).map(
        (item: TEnterprise, index: number) => ({
          navLink: `${routeUrls.sysadminChildren.enterprises}/${item.accountid}`,
          cellData: [
            // (listingQueryParams.users.page_size * (listingQueryParams.users.page - 1)) + index + 1, // sr_no by page
            item?.accountid, // account_id:
            item?.description || "-", // description:
            item?.contactname || "-", // contactname:
            item?.contactphone || "-", // contactphone:
            item?.timezone || "-", // timezone:
            `${item?.active_inactive_assets_count?.active_asset_count || 0}`, // active_assets:
            item?.isactive === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : item?.isactive === 0 ? (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ) : (
              "-"
            ), // active:
            <AdminFormFieldSubmit
              label={tMain("login_as")}
              variant="primary"
              customWrapperClass="w-24"
              onClick={(e) => handleLogin(item.accountid, e)}
              disabled={item?.isactive !== 1}
            />,
          ].filter((_, index) => columns[index].show),
          isActive: item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  showSysadminEnterprisesCreateNew: true,
                })
              );
            },
          })}
        />

        <ListingTableHeader
          heading={t("listing_heading")}
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={sysadminEnterprisesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />

        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Enterprises}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />

          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingEnterprises}
            listingQueryParams={sysadminEnterprisesQueryParams}
          />
          {!isFetchingEnterprises && (
            <Pagination
              pageSize={sysadminEnterprisesQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    sysadminEnterprises: {
                      ...sysadminEnterprisesQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / sysadminEnterprisesQueryParams.page_size)
                  : 1
              }
              forcePage={sysadminEnterprisesQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    sysadminEnterprises: {
                      ...sysadminEnterprisesQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={sysadminEnterprisesQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <SysadminEnterprisesCreateNew />
    </>
  );
};

export default ScreenSysadminEnterprises;
